import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { classNames, liveAnnounce, Level, analyticsTracker } from '@egds/react-core/utils';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { debounce } from 'throttle-debounce';
import styles from '../styles/egds-universal-shopping-carousel-inner-spacing-and-overflow.css';
import { EGDSSpacing } from '@egds/react-core/spacing';
import { EGDSPagingPrevButton, EGDSPagingNextButton } from '@egds/react-core/button';
const useIntersectionObservers = (elements, {
  threshold = 0,
  root = null,
  rootMargin = '0%'
}) => {
  const [entries, setEntries] = useState([]);
  const updateEntry = ([entry], index) => {
    setEntries(prevEntries => {
      const newEntries = [...prevEntries];
      newEntries[index] = entry;
      return newEntries;
    });
  };
  const trackedElementsId = JSON.stringify(elements.map(element => element.id));
  const observerParams = {
    threshold,
    root,
    rootMargin
  };
  const trackedObserverParams = JSON.stringify(observerParams);
  useEffect(() => {
    const hasIOSupport = Boolean(window.IntersectionObserver);
    if (!hasIOSupport) {
      return;
    }
    const observers = [];
    elements.forEach((element, index) => {
      observers[index] = new IntersectionObserver(entries => updateEntry(entries, index), observerParams);
      observers[index].observe(element);
    });
    if (elements) return () => {
      observers.forEach(observer => {
        observer.disconnect();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedElementsId, trackedObserverParams]);
  return entries;
};
const getClassNameList$5 = classNames.bind(styles);
const EGDSTeamCarouselItem = props => {
  const {
    children,
    wrapItems
  } = props;
  if (!React.isValidElement(children)) {
    return null;
  }
  const baseClassName = 'egds-team-carousel-item';
  const itemProps = {
    className: getClassNameList$5(`${children.props.className} ${baseClassName}`),
    'data-testid': 'carousel-item'
  };
  return jsx(Fragment, {
    children: wrapItems ? jsx("div", {
      ...itemProps,
      children: children
    }) : React.cloneElement(children, itemProps)
  });
};
EGDSTeamCarouselItem.displayName = 'EGDSTeamCarouselItem';
EGDSTeamCarouselItem.defaultProps = {
  wrapItems: true
};
const getClassNameList$4 = classNames.bind(styles);
const EGDSTeamCarouselItemList = props => {
  const {
    children,
    carouselListRef,
    wrapItems,
    spacing
  } = props;
  const baseClassName = 'egds-team-carousel-item-container';
  return jsx(EGDSSpacing, {
    ...spacing,
    children: jsx("div", {
      className: getClassNameList$4(baseClassName),
      ref: carouselListRef,
      "data-testid": "carousel-list",
      children: React.Children.map(children, (child, index) => {
        return jsx(EGDSTeamCarouselItem, {
          wrapItems: wrapItems,
          children: child
        }, index);
      })
    })
  });
};
EGDSTeamCarouselItemList.displayName = 'EGDSTeamCarouselItemList';
const getClassNameList$3 = classNames.bind(styles);
var EGDSTeamCarouselLocalAnalyticsIdType;
(function (EGDSTeamCarouselLocalAnalyticsIdType) {
  EGDSTeamCarouselLocalAnalyticsIdType["clickLeft"] = "clickLeft";
  EGDSTeamCarouselLocalAnalyticsIdType["clickRight"] = "clickRight";
  EGDSTeamCarouselLocalAnalyticsIdType["scrollLeft"] = "scrollLeft";
  EGDSTeamCarouselLocalAnalyticsIdType["scrollRight"] = "scrollRight";
})(EGDSTeamCarouselLocalAnalyticsIdType || (EGDSTeamCarouselLocalAnalyticsIdType = {}));
const EGDSTeamCarouselNav = props => {
  const {
    analytics,
    buttonText,
    carouselName,
    currentIndex,
    currentLastIndex,
    itemCount,
    itemsVisible,
    fullSheetVisible,
    loop,
    navigationIconId,
    onNextCb,
    onPrevCb,
    pagingOverlay,
    size,
    isRtl,
    hideNavOnScrollEnd,
    itemsVisibleAuto,
    isVisible
  } = props;
  const currentItemsVisible = itemsVisibleAuto ?? itemsVisible[size];
  const nextBtnRef = React.useRef(null);
  const prevBtnRef = React.useRef(null);
  // Fixes issue on mobile devices where touch events trigger paging events multiple times
  const DEBOUNCE_TIMER = typeof window !== 'undefined' && 'ontouchstart' in window ? 50 : 0;
  const clickLeftAnalytics = analytics && {
    localAnalyticsId: `${carouselName}.Carousel.${EGDSTeamCarouselLocalAnalyticsIdType.clickLeft}`,
    ...analytics
  };
  const clickRightAnalytics = analytics && {
    localAnalyticsId: `${carouselName}.Carousel.${EGDSTeamCarouselLocalAnalyticsIdType.clickRight}`,
    ...analytics
  };
  const focusNavigationBtn = ref => {
    /* istanbul ignore else */
    if (ref?.current) {
      ref.current.focus();
    }
  };
  const onPrev = debounce(DEBOUNCE_TIMER, () => {
    /* istanbul ignore else */
    onPrevCb?.();
    if (currentItemsVisible > 0 && currentIndex === 1) {
      focusNavigationBtn(prevBtnRef);
    }
  });
  const onNext = debounce(DEBOUNCE_TIMER, () => {
    /* istanbul ignore else */
    onNextCb?.();
    if (currentItemsVisible > 0 && currentIndex === itemCount - currentItemsVisible - 1) {
      focusNavigationBtn(nextBtnRef);
    }
  });
  const onKeyboardEvent = React.useCallback(e => {
    /* istanbul ignore else */
    if (isRtl) {
      /* istanbul ignore else */
      if (e.key === 'ArrowLeft') {
        onNext();
      } else if (e.key === 'ArrowRight') {
        onPrev();
      }
    } else if (e.key === 'ArrowLeft') {
      onPrev();
    } else if (e.key === 'ArrowRight') {
      onNext();
    }
  },
  // Linter states that onNext & onPrev should be dependencies here (eslint-plugin-react-hooks/exhaustive-deps)
  [isRtl, onNext, onPrev]);
  React.useEffect(() => {
    if (fullSheetVisible) {
      document.body.addEventListener('keydown', onKeyboardEvent);
      return () => {
        document.body.removeEventListener('keydown', onKeyboardEvent);
      };
    }
    // Linter states that onKeyboardEvent should be a dependency here (eslint-plugin-react-hooks/exhaustive-deps)
  }, [fullSheetVisible, onKeyboardEvent]);
  if (itemCount <= currentItemsVisible) {
    return null;
  }
  const isPrevBtnDisabled = !loop && currentIndex === 0;
  const isNextBtnDisabled = !loop && currentIndex === itemCount - currentItemsVisible || currentLastIndex === itemCount - 1;
  const isPrevBtnHidden = isVisible === false || hideNavOnScrollEnd && isPrevBtnDisabled;
  const isNextBtnHidden = isVisible === false || hideNavOnScrollEnd && isNextBtnDisabled;
  const prevBtnClassNames = getClassNameList$3('egds-team-carousel-button-paging', 'egds-team-carousel-button-prev', isPrevBtnHidden && 'egds-team-carousel-button-hidden');
  const nextBtnClassNames = getClassNameList$3('egds-team-carousel-button-paging', 'egds-team-carousel-button-next', isNextBtnHidden && 'egds-team-carousel-button-hidden');
  return jsxs("div", {
    className: getClassNameList$3('egds-team-carousel-controls'),
    children: [jsx(EGDSPagingPrevButton, {
      analytics: clickLeftAnalytics,
      className: prevBtnClassNames,
      iconId: navigationIconId?.prev,
      iconTitle: buttonText.prevButton,
      onClick: onPrev,
      domRef: prevBtnRef,
      "data-testid": "carousel-nav-prev",
      "aria-disabled": isPrevBtnDisabled || isPrevBtnHidden,
      "aria-label": buttonText.prevButton,
      tabIndex: isPrevBtnDisabled || isPrevBtnHidden ? -1 : 0,
      pagingOverlay: pagingOverlay
    }), jsx(EGDSPagingNextButton, {
      analytics: clickRightAnalytics,
      className: nextBtnClassNames,
      iconId: navigationIconId?.next,
      iconTitle: buttonText.nextButton,
      onClick: onNext,
      domRef: nextBtnRef,
      "data-testid": "carousel-nav-next",
      "aria-disabled": isNextBtnDisabled || isNextBtnHidden,
      "aria-label": buttonText.nextButton,
      tabIndex: isNextBtnDisabled || isNextBtnHidden ? -1 : 0,
      pagingOverlay: pagingOverlay
    })]
  });
};
EGDSTeamCarouselNav.displayName = 'EGDSTeamCarouselNav';
const EGDSTeamCarouselBreakPointSizeMap = [['sm', 'sm'], ['md', 'md'], ['lg', 'lg'], ['xl', 'xl']];
const EGDSTeamCarouselBreakPointSizes = new Map(EGDSTeamCarouselBreakPointSizeMap);
const EGDSTeamHeadingTagMap = [['h1', 'h1'], ['h2', 'h2'], ['h3', 'h3'], ['h4', 'h4'], ['h5', 'h5'], ['h6', 'h6']];
const EGDSTeamHeadingTags = new Map(EGDSTeamHeadingTagMap);
const getClassNameList$2 = classNames.bind(styles);
var EGDSTeamCarouselAutoWidthEvents;
(function (EGDSTeamCarouselAutoWidthEvents) {
  EGDSTeamCarouselAutoWidthEvents["clickLeft"] = "clickLeft";
  EGDSTeamCarouselAutoWidthEvents["clickRight"] = "clickRight";
  EGDSTeamCarouselAutoWidthEvents["scrollLeft"] = "scrollLeft";
  EGDSTeamCarouselAutoWidthEvents["scrollRight"] = "scrollRight";
})(EGDSTeamCarouselAutoWidthEvents || (EGDSTeamCarouselAutoWidthEvents = {}));
const CarouselAutoWidth = props => {
  const {
    buttonText,
    carouselHeaderLevel,
    carouselHeaderText,
    carouselHeaderVisible,
    carouselName,
    children,
    className,
    domRef,
    fullSheetVisible,
    hideNav,
    itemsVisible,
    itemsMaxHeight,
    l10n,
    navigationIconId,
    onChange,
    onIndexChange,
    pagingInset,
    pagingOverlay,
    startIndex,
    wrapItems,
    hideNavOnScrollEnd,
    itemViewedCallback,
    onNextButtonClick,
    onPrevButtonClick,
    onScrollEnd,
    isPopoverOpenList,
    spacing
  } = props;
  const [mounted, setMounted] = React.useState(false);
  const [isTouch, setIsTouch] = React.useState(false);
  const [isRtl, setIsRtl] = React.useState(false);
  const [size, setSize] = React.useState('sm');
  const [sizeInitialized, setSizeInitialized] = React.useState(false);
  const [isNavVisible, setIsNavVisible] = React.useState(false);
  const [marginInlineStart, setMarginInlineStart] = React.useState(0);
  const NAVIGATE_WITH_BTN = React.useRef(false); // Scrolling using a paging button
  const AVOID_FREE_SCROLL = React.useRef(false); // Free scrolling is scrolling the carousel without using the paging buttons
  const SCROLL_WITHOUT_ANIMATION = React.useRef(false);
  const WRAPPER = React.useRef(null);
  // Default style variables
  let peekWidth = 48;
  let itemPadding = 8;
  // Interaction Timer of resize handler
  const INTERACTION_TIMER = 200;
  const SCROLL_TIMER = 350;
  const wrapperRef = domRef ?? WRAPPER;
  const containerRef = React.useRef(null);
  const carouselListRef = React.useRef(null);
  const items = [...(carouselListRef.current?.children ?? [])];
  const entries = useIntersectionObservers(items, {});
  const isItemVisibleList = entries.map(entry => Boolean(entry?.isIntersecting));
  const fullyVisibleEntries = useIntersectionObservers(items, {
    threshold: 0.99
  });
  const isFullyVisibleList = fullyVisibleEntries.map(entry => Boolean(entry?.isIntersecting));
  const itemsVisibleCount = isItemVisibleList.reduce((count, isItemVisible) => isItemVisible ? count + 1 : count, 0);
  const isAnyPopoverOpen = isPopoverOpenList?.some(Boolean) ?? false;
  const itemCount = items?.length;
  // Below Values are reffed because we want them to be maintained between renders
  // Array that tracks which items have been viewed
  const itemsViewed = React.useRef([]);
  // Distance from origin of the left-most item (or right-most item for RTL)
  const positionWithinCarousel = React.useRef(0);
  // We use this to know if the carousel is going forwards or backwards
  const directionOfScrollIsForward = React.useRef(true);
  // We use this to track the leftmost item in the viewport's index
  const leftItemIndex = React.useRef(0);
  // We use this to track the rightmost item in the viewport's index
  const rightItemIndex = React.useRef(0);
  // Most recent requestAnimationFrame ID
  const requestID = React.useRef(0);
  // Most recent nav visibility timeout ID
  const navVisibilityTimeoutID = React.useRef(0);
  // We use this to track if the carousel is being hovered by a mouse
  const isHovered = React.useRef(false);
  // A11y related functions -- Starts -- DO WE EVEN NEED THESE?
  const announceCards = () => {
    if (containerRef.current) {
      const visibleCardHeadingsElements = containerRef.current.querySelectorAll(`
          .egds-team-carousel-item:not([data-hidden="true"]) h1:not([aria-hidden="true"]),
          .egds-team-carousel-item:not([data-hidden="true"]) h2:not([aria-hidden="true"]),
          .egds-team-carousel-item:not([data-hidden="true"]) h3:not([aria-hidden="true"]),
          .egds-team-carousel-item:not([data-hidden="true"]) h4:not([aria-hidden="true"]),
          .egds-team-carousel-item:not([data-hidden="true"]) h5:not([aria-hidden="true"]),
          .egds-team-carousel-item:not([data-hidden="true"]) h6:not([aria-hidden="true"])
        `);
      const visibleCardHeadings = Array.from(visibleCardHeadingsElements).map(heading => heading.innerText || heading.textContent);
      if (visibleCardHeadings.length > 0) {
        setTimeout(() => {
          liveAnnounce(l10n.formatText('carousel.cardChange', visibleCardHeadings.join(', ')), Level.POLITE);
        }, 200);
      } else {
        setTimeout(() => {
          liveAnnounce(l10n.formatText('gallerycarousel.imageChange', leftItemIndex.current + 1, itemCount, ''), Level.POLITE);
        }, 200);
      }
    }
  };
  const setTabIndex = () => {
    if (containerRef.current) {
      const carouselItems = containerRef.current.querySelectorAll('.egds-team-carousel-item');
      const carouselItemsArray = Array.from(carouselItems);
      carouselItemsArray.forEach(item => {
        const carouselElement = item.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        const carouselElementArray = Array.from(carouselElement);
        carouselElementArray.filter(elem => !elem.hasAttribute('aria-hidden')).forEach(elem => elem[item.hasAttribute('aria-hidden') ? 'setAttribute' : 'removeAttribute']('tabindex', '-1'));
      });
    }
  };
  // A11y related functions -- Ends
  // Carousel main functions -- Starts
  const showNav = () => {
    if (isHovered.current) {
      return;
    }
    clearTimeout(navVisibilityTimeoutID.current);
    setIsNavVisible(true);
    navVisibilityTimeoutID.current = window.setTimeout(() => setIsNavVisible(false), 2000);
  };
  const handleMouseEnter = () => {
    if (size === 'sm') {
      return;
    }
    isHovered.current = true;
    clearTimeout(navVisibilityTimeoutID.current);
    setIsNavVisible(true);
  };
  const handleMouseLeave = () => {
    if (size === 'sm') {
      return;
    }
    isHovered.current = false;
    navVisibilityTimeoutID.current = window.setTimeout(() => setIsNavVisible(false), 2000);
  };
  // istanbul ignore next
  const getIncrementalPosition = (time, positionToSnapTo, positionUserScrolledTo, range) => {
    // Quadratic easein-easeout function // refer to https://easings.net/#easeInOutQuad
    const curve = x => x < 0.5 ? 2 * x ** 2 : 1 - (-2 * x + 2) ** 2 / 2;
    const done = time >= SCROLL_TIMER;
    const position = done ? positionToSnapTo : positionUserScrolledTo + range * curve(time / SCROLL_TIMER);
    return {
      done,
      position
    };
  };
  const reverseRtlValue = value => isRtl ? value * -1 : value; // The scrollLeft method returns a negative value in RTL but our functions frequently expect a positive (or reverse) value
  // Scroll carousel to newPosition pixels from origin (which is the left edge for LTR and the right edge for RTL)
  const scrollCarousel = newPosition => {
    if (!containerRef.current) {
      return;
    }
    let startTimestamp;
    const containerElement = containerRef.current;
    const positionUserScrolledTo = reverseRtlValue(containerElement.scrollLeft);
    const range = newPosition - positionUserScrolledTo;
    // Incrementally advance the carousel using the curve value described above
    // istanbul ignore next
    const step = timestamp => {
      if (!startTimestamp) {
        startTimestamp = timestamp;
      }
      const {
        done,
        position
      } = getIncrementalPosition(timestamp - startTimestamp, newPosition, positionUserScrolledTo, range);
      containerElement.scrollLeft = reverseRtlValue(position); // This moves the carousel
      if (!done) {
        requestID.current = window.requestAnimationFrame(step);
      }
    };
    if (SCROLL_WITHOUT_ANIMATION.current) {
      // Occurs when startIndex is set
      SCROLL_WITHOUT_ANIMATION.current = false;
      containerElement.scrollLeft = reverseRtlValue(newPosition);
      setMounted(true);
    } else {
      window.cancelAnimationFrame(requestID.current);
      requestID.current = window.requestAnimationFrame(step);
    }
  };
  const onScroll = () => {
    if (!containerRef.current) {
      return;
    }
    const positionUserScrolledTo = reverseRtlValue(containerRef.current.scrollLeft); // Distance between origin and the position the user scrolled to
    if (positionUserScrolledTo > positionWithinCarousel.current) {
      directionOfScrollIsForward.current = true;
    } else {
      directionOfScrollIsForward.current = false;
    }
    positionWithinCarousel.current = positionUserScrolledTo;
    showNav();
  };
  const setViewportSize = () => {
    const carouselListElement = carouselListRef?.current;
    if (carouselListElement) {
      let carouselSize = getComputedStyle(carouselListElement).getPropertyValue('--size') || 'sm';
      if (carouselSize === 'xl' && !itemsVisible.xl) {
        carouselSize = 'lg'; // Backward compatibility if xl is not passed
      }
      setSize(carouselSize);
      if (!sizeInitialized) {
        setSizeInitialized(true);
      }
    }
  };
  const scrollToIndex = (index, scrollDirection) => {
    if (!carouselListRef.current || !containerRef.current) {
      return;
    }
    const {
      width: carouselWidth,
      left: carouselLeft
    } = carouselListRef.current.getBoundingClientRect();
    const {
      width: viewportWidth
    } = containerRef.current.getBoundingClientRect();
    const {
      left: targetItemLeft,
      right: targetItemRight
    } = items[index].getBoundingClientRect();
    const {
      right: firstItemRight
    } = items[0].getBoundingClientRect();
    let newPosition = 0;
    if (isRtl) {
      if (index === itemCount - 1) {
        newPosition = firstItemRight - targetItemLeft - viewportWidth;
      } else if (scrollDirection === EGDSTeamCarouselAutoWidthEvents.scrollRight && index > 0) {
        newPosition = Math.ceil(firstItemRight - (targetItemLeft + targetItemRight) / 2);
      } else if (scrollDirection === EGDSTeamCarouselAutoWidthEvents.scrollLeft) {
        newPosition = Math.floor(firstItemRight - viewportWidth - (targetItemLeft + targetItemRight) / 2);
      }
    } else if (index === itemCount - 1) {
      newPosition = Math.ceil(carouselWidth - viewportWidth);
    } else if (scrollDirection === EGDSTeamCarouselAutoWidthEvents.scrollRight) {
      newPosition = Math.ceil((targetItemLeft + targetItemRight) / 2 - viewportWidth - carouselLeft);
    } else if (scrollDirection === EGDSTeamCarouselAutoWidthEvents.scrollLeft && index > 0) {
      newPosition = Math.floor((targetItemLeft + targetItemRight) / 2 - carouselLeft);
    }
    NAVIGATE_WITH_BTN.current = true;
    scrollCarousel(newPosition);
  };
  const onPrevCb = () => {
    onPrevButtonClick?.();
    if (isRtl) {
      rightItemIndex.current = Math.max(rightItemIndex.current - 1, 0);
      scrollToIndex(rightItemIndex.current, EGDSTeamCarouselAutoWidthEvents.scrollRight);
    } else {
      leftItemIndex.current = Math.max(leftItemIndex.current - 1, 0);
      scrollToIndex(leftItemIndex.current, EGDSTeamCarouselAutoWidthEvents.scrollLeft);
    }
  };
  const onNextCb = () => {
    onNextButtonClick?.();
    if (isRtl) {
      leftItemIndex.current = Math.min(leftItemIndex.current + 1, itemCount - 1);
      scrollToIndex(leftItemIndex.current, EGDSTeamCarouselAutoWidthEvents.scrollLeft);
    } else {
      rightItemIndex.current = Math.min(rightItemIndex.current + 1, itemCount - 1);
      scrollToIndex(rightItemIndex.current, EGDSTeamCarouselAutoWidthEvents.scrollRight);
    }
  };
  const setTokenStyles = () => {
    const wrapperElement = wrapperRef.current;
    if (wrapperElement) {
      peekWidth = parseInt(getComputedStyle(wrapperElement).getPropertyValue('--peek-width'), 10) * 16 || peekWidth;
      itemPadding = parseInt(getComputedStyle(wrapperElement).getPropertyValue('--item-padding'), 10) * 16 || itemPadding;
    }
  };
  // useEffect
  const trackedIsItemVisibleList = JSON.stringify(isItemVisibleList);
  React.useEffect(() => {
    leftItemIndex.current = isRtl ? isItemVisibleList.lastIndexOf(true) : isItemVisibleList.indexOf(true);
    rightItemIndex.current = isRtl ? isItemVisibleList.indexOf(true) : isItemVisibleList.lastIndexOf(true);
    isItemVisibleList.forEach((isItemVisible, index) => {
      if (isItemVisible && !itemsViewed.current[index]) {
        itemViewedCallback?.(index);
        itemsViewed.current[index] = true;
      }
    });
  }, [trackedIsItemVisibleList]);
  React.useEffect(() => {
    if (mounted) {
      announceCards();
    }
    onIndexChange?.(leftItemIndex.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizeInitialized]);
  React.useEffect(() => {
    // Initialize viewport size
    setViewportSize();
    // Ensure that scrollLeft position is reset on mount
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
    }
    // Change carousel's index to startIndex without triggering scroll animation
    if (startIndex) {
      SCROLL_WITHOUT_ANIMATION.current = true;
    } else {
      setMounted(true);
    }
    // Initialize token values set from CSS
    setTokenStyles();
    if ('ontouchstart' in window) {
      setIsTouch(true);
    }
    if (document.querySelector('html').getAttribute('dir') === 'rtl') {
      setIsRtl(true);
    }
    // Adds empty aria-live region to page to ensure Jaws screen reader
    // announces on initial key press. Relates to https://jira.expedia.biz/browse/WCAG-8258
    liveAnnounce('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizeInitialized]);
  React.useEffect(() => {
    let windowWidth = window.innerWidth;
    const debounceResizeHandler = debounce(INTERACTION_TIMER, () => {
      if (windowWidth === window.innerWidth) {
        return;
      }
      windowWidth = window.innerWidth;
      setViewportSize();
    });
    window.addEventListener('resize', debounceResizeHandler);
    return () => window.removeEventListener('resize', debounceResizeHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);
  React.useEffect(() => {
    onChange?.(containerRef);
    setTabIndex();
  });
  React.useEffect(() => {
    if (isAnyPopoverOpen) {
      setMarginInlineStart(-positionWithinCarousel.current);
    } else {
      setMarginInlineStart(0);
    }
  }, [isAnyPopoverOpen]);
  // Prevents tab events from activating scrolling
  const onKeyboardEvent = React.useCallback(e => {
    if (e.key) {
      NAVIGATE_WITH_BTN.current = true;
      AVOID_FREE_SCROLL.current = false;
    }
  }, []);
  React.useEffect(() => {
    document.body.addEventListener('keydown', onKeyboardEvent);
    return () => {
      document.body.removeEventListener('keydown', onKeyboardEvent);
    };
  }, [onKeyboardEvent]);
  const isLastItemFullyVisible = isFullyVisibleList[isFullyVisibleList.length - 1];
  const isLastItemVisible = isItemVisibleList[isItemVisibleList.length - 1];
  const shouldTriggerOnScrollEnd = React.useRef(true);
  React.useEffect(() => {
    if (shouldTriggerOnScrollEnd.current && isLastItemFullyVisible) {
      onScrollEnd?.();
      shouldTriggerOnScrollEnd.current = false;
    } else if (!isLastItemVisible) {
      shouldTriggerOnScrollEnd.current = true;
    }
  }, [isLastItemFullyVisible, isLastItemVisible]);
  const styleObj = {
    marginInlineStart
  };
  styleObj['--carousel-item-count'] = itemsVisible[size];
  styleObj['--carousel-item-count-sm'] = itemsVisible.sm;
  styleObj['--carousel-item-count-md'] = itemsVisible.md;
  styleObj['--carousel-item-count-lg'] = itemsVisible.lg;
  styleObj['--carousel-item-count-xl'] = itemsVisible.xl ? itemsVisible.xl : itemsVisible.lg;
  const cssClassNames = getClassNameList$2('egds-team-carousel', 'no-peek', !pagingInset && 'no-inset', !isTouch && 'no-touch', itemsMaxHeight && 'items-max-height', className, {
    [size]: !!size && typeof window !== 'undefined'
  }, isAnyPopoverOpen && 'popover-sheet-open');
  const navProps = {
    buttonText,
    carouselName,
    currentIndex: isRtl ? rightItemIndex.current : leftItemIndex.current,
    currentLastIndex: isRtl ? leftItemIndex.current : rightItemIndex.current,
    itemCount,
    itemsVisible,
    fullSheetVisible,
    navigationIconId,
    onNextCb,
    onPrevCb,
    pagingOverlay,
    size,
    isRtl,
    hideNavOnScrollEnd,
    itemsVisibleAuto: itemsVisibleCount,
    isVisible: isNavVisible
  };
  const CarouselHeaderLevel = carouselHeaderLevel && EGDSTeamHeadingTags.get(carouselHeaderLevel) || EGDSTeamHeadingTags.get('h3');
  return jsxs("div", {
    className: cssClassNames,
    ref: wrapperRef,
    "data-testid": "carousel-wrapper",
    children: [carouselHeaderText && jsx(CarouselHeaderLevel, {
      className: getClassNameList$2({
        [`is-visually-hidden`]: !carouselHeaderVisible
      }),
      children: carouselHeaderText
    }), jsx("div", {
      className: getClassNameList$2(`${isAnyPopoverOpen ? '' : 'egds-team-carousel-container'}`),
      "aria-label": carouselHeaderText,
      ref: containerRef,
      onScroll: onScroll,
      "data-testid": "carousel-container",
      style: styleObj,
      onClick: showNav,
      onKeyDown: showNav,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      children: jsx(EGDSTeamCarouselItemList, {
        wrapItems: wrapItems,
        carouselListRef: carouselListRef,
        spacing: spacing,
        children: children
      })
    }), !hideNav && jsx(EGDSTeamCarouselNav, {
      ...navProps
    })]
  });
};
const EGDSTeamCarouselAutoWidth = props => {
  const l10n = useLocalization();
  return jsx(CarouselAutoWidth, {
    l10n: l10n,
    ...props
  });
};
EGDSTeamCarouselAutoWidth.displayName = 'EGDSTeamCarouselAutoWidth';
EGDSTeamCarouselAutoWidth.defaultProps = {
  hideNav: false,
  itemsMaxHeight: false,
  navigationIconId: {
    next: 'next-button',
    prev: 'prev-button'
  },
  pagingInset: false,
  pagingOverlay: false,
  startIndex: 0,
  wrapItems: true,
  hideNavOnScrollEnd: false
};
const getClassNameList$1 = classNames.bind(styles);
const CarouselFixedWidth = props => {
  const {
    analytics,
    buttonText,
    carouselHeaderLevel,
    carouselHeaderText,
    carouselHeaderVisible,
    carouselName,
    carouselRef,
    children,
    className,
    domRef,
    fullSheetVisible,
    hideNav,
    itemsVisible,
    itemsMaxHeight,
    l10n,
    loop,
    navigationIconId,
    onChange,
    onIndexChange,
    pageBy = 1,
    pagingInset,
    pagingOverlay,
    peek,
    shouldSnapOnScroll,
    startIndex,
    stretchToFill,
    wrapItems,
    hideNavOnScrollEnd
  } = props;
  const [mounted, setMounted] = React.useState(false);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isTouch, setIsTouch] = React.useState(false);
  const [isRtl, setIsRtl] = React.useState(false);
  const [size, setSize] = React.useState('sm');
  const [sizeInitialized, setSizeInitialized] = React.useState(false);
  const NAVIGATE_WITH_BTN = React.useRef(false); // Scrolling using a paging button
  const AVOID_FREE_SCROLL = React.useRef(false); // Free scrolling is scrolling the carousel without using the paging buttons
  const SCROLL_WITHOUT_ANIMATION = React.useRef(false);
  const WRAPPER = React.useRef(null);
  // Default style variables
  let peekWidth = 48;
  let itemPadding = 12;
  // Interaction Timer of resize handler
  const INTERACTION_TIMER = 200;
  const SCROLL_TIMER = 350;
  const itemCount = React.Children.count(children);
  const wrapperRef = domRef ?? WRAPPER;
  const containerRef = React.useRef(null);
  const carouselListRef = React.useRef(null);
  // This can be non-nullable, since setViewportSize will always set a valid size
  const currentItemsVisible = itemsVisible[size];
  // Below Values are reffed because we want them to be maintained between renders
  const viewed = React.useRef([]);
  // Scroll distance is the distance between the start of one card and the next.
  const scrollDistance = React.useRef(0);
  // Distance from origin of the left-most item (or right-most item for RTL)
  const positionWithinCarousel = React.useRef(0);
  // We use this to know if the carousel is going forwards or backwards
  const directionOfScrollIsForward = React.useRef(true);
  // A11y related functions -- Starts -- DO WE EVEN NEED THESE?
  const announceCards = () => {
    if (containerRef.current) {
      const visibleCardHeadingsElements = containerRef.current.querySelectorAll(`
          .egds-team-carousel-item:not([data-hidden="true"]) h1:not([aria-hidden="true"]),
          .egds-team-carousel-item:not([data-hidden="true"]) h2:not([aria-hidden="true"]),
          .egds-team-carousel-item:not([data-hidden="true"]) h3:not([aria-hidden="true"]),
          .egds-team-carousel-item:not([data-hidden="true"]) h4:not([aria-hidden="true"]),
          .egds-team-carousel-item:not([data-hidden="true"]) h5:not([aria-hidden="true"]),
          .egds-team-carousel-item:not([data-hidden="true"]) h6:not([aria-hidden="true"])
        `);
      const visibleCardHeadings = Array.from(visibleCardHeadingsElements).map(heading => heading.innerText || heading.textContent);
      if (visibleCardHeadings.length > 0) {
        setTimeout(() => {
          liveAnnounce(l10n.formatText('carousel.cardChange', visibleCardHeadings.join(', ')), Level.POLITE);
        }, 200);
      } else {
        setTimeout(() => {
          liveAnnounce(l10n.formatText('gallerycarousel.imageChange', currentIndex + 1, itemCount, ''), Level.POLITE);
        }, 200);
      }
    }
  };
  const setTabIndex = () => {
    if (containerRef.current) {
      const carouselItems = containerRef.current.querySelectorAll('.egds-team-carousel-item');
      const carouselItemsArray = Array.from(carouselItems);
      carouselItemsArray.forEach(item => {
        const carouselElement = item.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        const carouselElementArray = Array.from(carouselElement);
        carouselElementArray.filter(elem => !elem.hasAttribute('aria-hidden')).forEach(elem => elem[item.hasAttribute('aria-hidden') ? 'setAttribute' : 'removeAttribute']('tabindex', '-1'));
      });
    }
  };
  // A11y related functions -- Ends
  // Analytics functions -- Starts
  const viewItemAnalytics = () => {
    let vis = currentItemsVisible;
    if (peek) {
      vis += 1;
    }
    for (let i = 0; i < vis && currentIndex + i < React.Children.count(children); ++i) {
      if (!viewed.current[currentIndex + i]) {
        viewed.current[currentIndex + i] = true;
        if (typeof props.itemViewedCallback === 'function') {
          props.itemViewedCallback(currentIndex + i);
        }
        analytics && analyticsTracker(analytics, `${carouselName}.Carousel.itemViewed.${currentIndex + i}`, '');
      }
    }
  };
  const scrollAnalytics = React.useCallback(scrollRight => {
    analytics && analyticsTracker(analytics, `${carouselName}.Carousel.${scrollRight ? EGDSTeamCarouselLocalAnalyticsIdType.scrollRight : EGDSTeamCarouselLocalAnalyticsIdType.scrollLeft}`, '');
  }, [analytics, carouselName]);
  // Analytics functions -- Ends
  // Carousel main functions -- Starts
  const getCurrentIndex = () => {
    if (containerRef.current) {
      const containerElement = containerRef.current;
      const {
        scrollLeft,
        scrollWidth
      } = containerElement;
      const carouselWidth = containerElement.getBoundingClientRect().width;
      const childWidth = scrollDistance.current;
      const distanceFromOrigin = Math.abs(scrollLeft);
      // Edge case for last item of carousel in view along with peeking last element of previous frame.
      // When scroll reaches the end of container then scrollLeft should be equal to scrollWidth - carouselWidth
      // but as subpixels are rounded to nearest integer this calc fails so added -5 to overcome it
      // Edge case only applies when there is more than one item
      const edgeCase = itemCount > 1 && distanceFromOrigin > Math.round(scrollWidth - carouselWidth - 5) ? 1 : 0;
      return Math.floor(distanceFromOrigin / childWidth) + edgeCase;
    }
    return 0;
  };
  const incrementIndexAsNeeded = index => {
    /* istanbul ignore else */
    if (directionOfScrollIsForward.current === true) {
      return index + 1;
    }
    return index;
  };
  const getIndexToSnapTo = index => {
    const maxIndex = itemCount - currentItemsVisible;
    const newIndex = incrementIndexAsNeeded(index);
    if (newIndex > maxIndex) {
      return maxIndex;
    }
    return newIndex;
  };
  const setScrollingForwardRef = position => {
    if (position > positionWithinCarousel.current) {
      directionOfScrollIsForward.current = true;
    } else {
      directionOfScrollIsForward.current = false;
    }
  };
  /* istanbul ignore next */
  const getIncrementalPosition = (time, positionToSnapTo, positionUserScrolledTo, range) => {
    // Quadratic easein-easeout function // refer to https://easings.net/#easeInOutQuad
    const curve = x => x < 0.5 ? 2 * x ** 2 : 1 - (-2 * x + 2) ** 2 / 2;
    const done = time >= SCROLL_TIMER;
    const position = done ? positionToSnapTo : positionUserScrolledTo + range * curve(time / SCROLL_TIMER);
    return {
      done,
      position
    };
  };
  const reverseRtlValue = value => isRtl ? value * -1 : value; // The scrollLeft method returns a negative value in RTL but our functions frequently expect a positive (or reverse) value
  // Scroll carousel to newPosition pixels from origin (which is the left edge for LTR and the right edge for RTL)
  const scrollCarousel = newPosition => {
    let startTimestamp;
    if (containerRef.current) {
      const containerElement = containerRef.current;
      const positionUserScrolledTo = reverseRtlValue(containerElement.scrollLeft);
      const range = newPosition - positionUserScrolledTo;
      // Incrementally advance the carousel using the curve value described above
      // istanbul ignore next
      const step = timestamp => {
        if (!startTimestamp) {
          startTimestamp = timestamp;
        }
        const {
          done,
          position
        } = getIncrementalPosition(timestamp - startTimestamp, newPosition, positionUserScrolledTo, range);
        containerElement.scrollLeft = reverseRtlValue(position); // This moves the carousel
        if (!done) {
          window.requestAnimationFrame(step);
        }
      };
      if (SCROLL_WITHOUT_ANIMATION.current) {
        // Occurs when startIndex is set
        SCROLL_WITHOUT_ANIMATION.current = false;
        containerElement.scrollLeft = reverseRtlValue(newPosition);
        setMounted(true);
      } else {
        window.requestAnimationFrame(step);
      }
      scrollAnalytics(range > 0);
      positionWithinCarousel.current = newPosition;
    }
  };
  const scrollCarouselWithNoSnap = () => {
    if (containerRef.current) {
      const positionUserScrolledTo = containerRef.current.scrollLeft;
      const isRightScroll = positionUserScrolledTo - positionWithinCarousel.current > 0;
      scrollAnalytics(isRightScroll);
      positionWithinCarousel.current = positionUserScrolledTo;
    }
  };
  // Adding this ignore comment to prevent this issue https://jira.expedia.biz/browse/EGDS-3032
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onScroll = React.useCallback(debounce(INTERACTION_TIMER, () => {
    if (AVOID_FREE_SCROLL.current) {
      AVOID_FREE_SCROLL.current = false;
      return false;
    }
    if (containerRef.current) {
      const carouselElement = containerRef.current;
      const positionUserScrolledTo = reverseRtlValue(carouselElement.scrollLeft); // Distance between origin and the position the user scrolled to
      // Cancel snap to scroll if scroll difference is off by just a rounding error, or if it is a tab event
      if (Math.abs(positionUserScrolledTo - positionWithinCarousel.current) < 1 || NAVIGATE_WITH_BTN.current) {
        return;
      }
      setScrollingForwardRef(positionUserScrolledTo);
      const childWidth = scrollDistance.current;
      const snapToIndex = getIndexToSnapTo(getCurrentIndex());
      const positionToSnapTo = childWidth * snapToIndex; // Distance between origin and the new position to snap to
      const range = positionToSnapTo - positionUserScrolledTo;
      const maxScrollLeft = carouselElement.scrollWidth - carouselElement.clientWidth;
      // Because this function is attached to the onScroll property of the component and calls a function which scrolls the carousel,
      // the onScroll function will always run twice, which is why we need to prevent it from scrolling a second time.
      if (range === 0 && positionUserScrolledTo > 0 && positionUserScrolledTo < maxScrollLeft) {
        return;
      }
      if (shouldSnapOnScroll) {
        const effectivePeekWidth = peek && snapToIndex !== 0 ? (peekWidth + itemPadding) / 2 : 0;
        scrollCarousel(positionToSnapTo - effectivePeekWidth);
      } else {
        scrollCarouselWithNoSnap();
      }
      setCurrentIndex(snapToIndex);
    }
  }), [size, isRtl]);
  // Prevents tab events from activating scrolling
  const onKeyboardEvent = React.useCallback(e => {
    if (e.key) {
      NAVIGATE_WITH_BTN.current = true;
      AVOID_FREE_SCROLL.current = false;
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [size, isRtl]);
  React.useEffect(() => {
    document.body.addEventListener('keydown', onKeyboardEvent);
    return () => {
      document.body.removeEventListener('keydown', onKeyboardEvent);
    };
  }, [onKeyboardEvent]);
  const setScrollDistance = () => {
    if (containerRef.current) {
      const carouselWidth = containerRef.current.getBoundingClientRect().width;
      const effectivePeekWidth = peek ? itemPadding + peekWidth : 0;
      scrollDistance.current = (carouselWidth - (currentItemsVisible - 1) * itemPadding - effectivePeekWidth) / currentItemsVisible + itemPadding;
    }
  };
  const setViewportSize = () => {
    const carouselListElement = carouselListRef?.current;
    if (carouselListElement) {
      setScrollDistance();
      let carouselSize = getComputedStyle(carouselListElement).getPropertyValue('--size') || 'sm';
      if (carouselSize === 'xl' && !itemsVisible.xl) {
        carouselSize = 'lg'; // Backward compatibility if xl is not passed
      }
      setSize(carouselSize);
      if (!sizeInitialized) setSizeInitialized(true);
    }
  };
  const page = direction => {
    const capPageBy = pageBy > currentItemsVisible ? currentItemsVisible : pageBy;
    let index = direction === 1 ? currentIndex + Number(capPageBy) : currentIndex - Number(capPageBy);
    const limit = itemCount - currentItemsVisible;
    if (index < 0) {
      index = loop ? itemCount - 1 : 0;
    } else if (index === limit) {
      index = limit;
    } else if (index > limit) {
      index = loop ? 0 : limit;
    }
    NAVIGATE_WITH_BTN.current = true;
    setCurrentIndex(index);
  };
  const onPrevCb = () => {
    page(-1);
  };
  const onNextCb = () => {
    page(1);
  };
  // Changes the carousel's current index
  const changeIndex = index => {
    // This is set to bypass scroll event callbacks
    NAVIGATE_WITH_BTN.current = true;
    let newIndex;
    const maxIndex = itemCount - currentItemsVisible;
    if (index < 0) {
      newIndex = 0;
    } else if (index >= maxIndex) {
      newIndex = maxIndex;
    } else {
      newIndex = index;
    }
    setCurrentIndex(newIndex);
  };
  const setTokenStyles = () => {
    const wrapperElement = wrapperRef.current;
    if (wrapperElement) {
      peekWidth = parseInt(getComputedStyle(wrapperElement).getPropertyValue('--peek-width'), 10) * 16 || peekWidth;
      itemPadding = parseInt(getComputedStyle(wrapperElement).getPropertyValue('--item-padding'), 10) * 16 || itemPadding;
    }
  };
  const renderCarousel = () => {
    const styleObj = {};
    styleObj['--carousel-item-count'] = itemsVisible[size];
    styleObj['--carousel-item-count-sm'] = itemsVisible.sm;
    styleObj['--carousel-item-count-md'] = itemsVisible.md;
    styleObj['--carousel-item-count-lg'] = itemsVisible.lg;
    styleObj['--carousel-item-count-xl'] = itemsVisible.xl ? itemsVisible.xl : itemsVisible.lg;
    const cssClassNames = getClassNameList$1('egds-team-carousel', (!peek || !(currentItemsVisible < itemCount)) && 'no-peek', !pagingInset && 'no-inset', !isTouch && 'no-touch', itemsMaxHeight && 'items-max-height', className, {
      [size]: !!size && typeof window !== 'undefined'
    });
    const navProps = {
      analytics,
      buttonText,
      carouselName,
      currentIndex,
      itemCount,
      itemsVisible,
      fullSheetVisible,
      loop,
      navigationIconId,
      onNextCb,
      onPrevCb,
      pagingOverlay,
      size,
      isRtl,
      hideNavOnScrollEnd
    };
    const CarouselHeaderLevel = carouselHeaderLevel && EGDSTeamHeadingTags.get(carouselHeaderLevel) || EGDSTeamHeadingTags.get('h3');
    return jsxs("div", {
      className: cssClassNames,
      ref: wrapperRef,
      "data-testid": "carousel-wrapper",
      children: [carouselHeaderText && jsx(CarouselHeaderLevel, {
        className: getClassNameList$1({
          [`is-visually-hidden`]: !carouselHeaderVisible
        }),
        children: carouselHeaderText
      }), jsx("div", {
        className: getClassNameList$1('egds-team-carousel-container'),
        "aria-label": carouselHeaderText,
        ref: containerRef,
        onScroll: onScroll,
        "data-testid": "carousel-container",
        style: styleObj,
        tabIndex: 0,
        children: jsx(EGDSTeamCarouselItemList, {
          itemsVisible: itemsVisible,
          size: size,
          currentIndex: currentIndex,
          peek: peek,
          wrapItems: wrapItems,
          stretchToFill: stretchToFill,
          carouselListRef: carouselListRef,
          children: children
        })
      }), !hideNav && jsx(EGDSTeamCarouselNav, {
        ...navProps
      })]
    });
  };
  // Hooks
  React.useEffect(() => {
    mounted && announceCards();
    if (sizeInitialized) viewItemAnalytics();
    // Turn off free scroll and yet advance the carousel
    if (NAVIGATE_WITH_BTN.current) {
      NAVIGATE_WITH_BTN.current = false;
      AVOID_FREE_SCROLL.current = true;
      const effectivePeekWidth = peek && currentIndex !== 0 ? (peekWidth + itemPadding) / 2 / currentIndex : 0;
      scrollCarousel(currentIndex * (scrollDistance.current - effectivePeekWidth));
    }
    onIndexChange && onIndexChange(currentIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, sizeInitialized]);
  React.useEffect(() => {
    // Initialize viewport size
    setViewportSize();
    // Ensure that scrollLeft position is reset on mount
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
    }
    // Change carousel's index to startIndex without triggering scroll animation
    if (startIndex) {
      SCROLL_WITHOUT_ANIMATION.current = true;
      changeIndex(startIndex);
    } else {
      setMounted(true);
    }
    // Initialize token values set from CSS
    setTokenStyles();
    'ontouchstart' in window && setIsTouch(true);
    document.querySelector('html').getAttribute('dir') === 'rtl' && setIsRtl(true);
    // Adds empty aria-live region to page to ensure Jaws screen reader
    // announces on initial key press. Relates to https://jira.expedia.biz/browse/WCAG-8258
    liveAnnounce('');
    if (sizeInitialized) viewItemAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizeInitialized]);
  React.useEffect(() => {
    let windowWidth = window.innerWidth;
    const debounceResizeHandler = debounce(INTERACTION_TIMER, () => {
      if (windowWidth === window.innerWidth) {
        return;
      }
      windowWidth = window.innerWidth;
      setViewportSize();
      const updatedCurrentIndexOnResize = getCurrentIndex();
      // Ensures that upon resize, the carousel will scroll to a position where the updated index slide will completely show.
      setCurrentIndex(updatedCurrentIndexOnResize);
      const effectivePeekWidth = peek && updatedCurrentIndexOnResize !== 0 ? (peekWidth + itemPadding) / 2 / updatedCurrentIndexOnResize : 0;
      scrollCarousel(updatedCurrentIndexOnResize * (scrollDistance.current - effectivePeekWidth));
    });
    window.addEventListener('resize', debounceResizeHandler);
    setScrollDistance();
    return () => window.removeEventListener('resize', debounceResizeHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, currentIndex]);
  React.useEffect(() => {
    onChange && onChange(containerRef);
    setTabIndex();
  });
  React.useImperativeHandle(carouselRef, () => ({
    changeIndex: index => changeIndex(index)
  }));
  // Carousel main functions -- Ends
  return renderCarousel();
};
const EGDSTeamCarouselFixedWidth = props => {
  const l10n = useLocalization();
  return jsx(CarouselFixedWidth, {
    l10n: l10n,
    ...props
  });
};
EGDSTeamCarouselFixedWidth.displayName = 'EGDSTeamCarouselFixedWidth';
EGDSTeamCarouselFixedWidth.defaultProps = {
  hideNav: false,
  itemsMaxHeight: false,
  navigationIconId: {
    next: 'next-button',
    prev: 'prev-button'
  },
  pagingInset: false,
  pagingOverlay: false,
  shouldSnapOnScroll: false,
  startIndex: 0,
  stretchToFill: true,
  wrapItems: true,
  hideNavOnScrollEnd: false
};

/**
 * # 🚨 Please read the [Team Component Onboarding Guide](https://confluence.expedia.biz/display/EGDesignSystem/Getting+Started%3A+Team+Component+Onboarding+Guide), fill out the intake request found in the onboarding guide, and fill out the details below before creating a new team component. You can delete these sirens when you are done! 🚨
 * ### Team: The team responsible for ownership of this component
 * ### Reporter: The point of contact for this component
 * ### Designer: The designer responsible for this component
 * ### Description: A description of your component
 * ### Slack Channel: [#your-team-slack](https://slack.com)
 */
const CarouselInnerSpacingAndOverflow = props => {
  const {
    itemsVisible,
    children,
    ...restProps
  } = props;
  if (itemsVisible.sm === 'auto' && itemsVisible.md === 'auto' && itemsVisible.lg === 'auto' && itemsVisible.xl === 'auto') {
    return jsx(EGDSTeamCarouselAutoWidth, {
      ...restProps,
      itemsVisible: itemsVisible,
      children: children
    });
  }
  // Mixed auto and fixed widths for different breakpoints are not currently supported
  if (itemsVisible.sm === 'auto' || itemsVisible.md === 'auto' || itemsVisible.lg === 'auto' || itemsVisible.xl === 'auto') {
    return null;
  }
  return jsx(EGDSTeamCarouselFixedWidth, {
    ...restProps,
    itemsVisible: itemsVisible,
    children: children
  });
};
CarouselInnerSpacingAndOverflow.displayName = 'CarouselInnerSpacingAndOverflow';
CarouselInnerSpacingAndOverflow.defaultProps = {
  hideNav: false,
  itemsMaxHeight: false,
  navigationIconId: {
    next: 'next-button',
    prev: 'prev-button'
  },
  pagingInset: false,
  pagingOverlay: false,
  shouldSnapOnScroll: false,
  startIndex: 0,
  stretchToFill: true,
  wrapItems: true,
  hideNavOnScrollEnd: false
};
const getClassNameList = classNames.bind(styles);
const CarouselPaginationDots = props => {
  const {
    activeIndex,
    changeIndex,
    inset,
    itemCount,
    l10n
  } = props;
  const containerRef = React.createRef();
  const {
    formatText
  } = l10n;
  // For now setting maximum pagination dots to 5, but this could be made a prop
  const maxDots = 5;
  const baseClassName = 'egds-team-carousel-pagination-dots';
  const isAtBeginning = (currentIndex = activeIndex) => currentIndex <= Math.floor(maxDots / 2);
  const isAtEnd = (currentIndex = activeIndex) => currentIndex >= itemCount - 1 - Math.floor(maxDots / 2);
  const calculateStartIndex = newActiveIndex => {
    let newStartIndex;
    if (isAtBeginning(newActiveIndex)) {
      newStartIndex = 0;
    } else if (isAtEnd(newActiveIndex)) {
      newStartIndex = itemCount - maxDots;
    } else {
      newStartIndex = activeIndex - Math.floor(maxDots / 2);
    }
    return newStartIndex;
  };
  const [startIndex, setStartIndex] = React.useState(calculateStartIndex);
  // Update starting index whenever active index changes
  React.useEffect(() => {
    setStartIndex(calculateStartIndex());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);
  // When updating the start index, scroll horizontally until the start index button is visible
  React.useEffect(() => {
    // Dot width * number of dots to scroll past * space between those dots
    const scrollDistance = 12 * startIndex;
    if (containerRef && containerRef.current) {
      containerRef.current.scrollLeft = scrollDistance;
    }
  }, [containerRef, startIndex]);
  const handleClick = e => {
    const clickedIndex = Number(e.currentTarget?.value);
    calculateStartIndex(clickedIndex);
    changeIndex && changeIndex(clickedIndex);
  };
  const renderDots = () => {
    const paginationDots = [];
    for (let index = 0; index < itemCount; index++) {
      const isFirstDot = index === startIndex;
      const isLastDot = index === startIndex + maxDots - 1;
      const shouldFocus = activeIndex === index;
      // Truncate: if first or last pagination dot AND there are more pages in that direction
      const shouldTruncate = isFirstDot && !isAtBeginning() || isLastDot && !isAtEnd();
      const dotBaseClassName = `${baseClassName}-icon`;
      const dotClassNames = getClassNameList(dotBaseClassName, {
        [`${dotBaseClassName}-focus`]: shouldFocus,
        [`${dotBaseClassName}-truncate`]: shouldTruncate,
        [`${dotBaseClassName}-default`]: !shouldTruncate && !shouldFocus,
        [`${dotBaseClassName}-inset`]: inset,
        [`${dotBaseClassName}-offset`]: !inset
      });
      const isOutOfView = index < startIndex || index >= startIndex + maxDots;
      paginationDots.push(jsx("button", {
        "aria-label": formatText('carousel.indexAndTotal', index + 1, itemCount),
        "aria-hidden": isOutOfView,
        "aria-current": index === activeIndex ? 'location' : false,
        tabIndex: isOutOfView ? -1 : 0,
        type: "button",
        className: getClassNameList(`${baseClassName}-button`),
        onClick: e => handleClick(e),
        value: index,
        children: jsx("div", {
          className: getClassNameList(dotClassNames),
          "aria-hidden": "true"
        })
      }, index));
    }
    return paginationDots;
  };
  return jsx("div", {
    ref: containerRef,
    className: getClassNameList(`${baseClassName}-container`),
    children: renderDots()
  });
};
const EGDSTeamCarouselPaginationDots = props => {
  const l10n = useLocalization();
  return jsx(CarouselPaginationDots, {
    l10n: l10n,
    ...props
  });
};
EGDSTeamCarouselPaginationDots.displayName = 'EGDSTeamCarouselPaginationDots';
export { CarouselAutoWidth, CarouselFixedWidth, CarouselInnerSpacingAndOverflow, CarouselPaginationDots, EGDSTeamCarouselAutoWidth, EGDSTeamCarouselAutoWidthEvents, EGDSTeamCarouselBreakPointSizes, EGDSTeamCarouselFixedWidth, EGDSTeamCarouselItem, EGDSTeamCarouselItemList, EGDSTeamCarouselLocalAnalyticsIdType, EGDSTeamCarouselNav, EGDSTeamCarouselPaginationDots, EGDSTeamHeadingTags };